import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from 'react-i18next';
import { fuzzyScoreFields } from "./helpers/fuzzy";

interface Ingredient {
  id: string;
  common_name: string;
  supplier: string | null;
  [key: string]: any;
}

interface Props {
  ingredients: Ingredient[];
  position: { top: number; left: number };
  currentValue: string | null;
  onSelect: (id: string) => void;
  onClose: () => void;
}

/**
 * Standalone ingredient picker dropdown — NOT an AG Grid cell editor.
 * Renders as a portal positioned at `position`. Calls `onSelect` with the
 * chosen ingredient ID, or `onClose` to dismiss without selecting.
 */
export default function IngredientPicker({
  ingredients,
  position,
  currentValue,
  onSelect,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return ingredients;
    // Typo-tolerant: substring matches first, then fuzzy matches by score.
    return ingredients
      .map((ing) => ({
        ing,
        score: fuzzyScoreFields(search, [
          ing.id,
          ing.common_name,
          ing.fda_name,
          ing.supplier,
        ]),
      }))
      .filter((x) => x.score >= 0)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.ing);
  }, [search, ingredients]);

  useEffect(() => {
    setHighlightIdx(0);
  }, [filtered.length]);

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.children[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: "nearest" });
  }, [highlightIdx]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filtered.length > 0 && highlightIdx < filtered.length) {
          onSelect(filtered[highlightIdx].id);
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((i) => Math.max(i - 1, 0));
      }
    },
    [filtered, highlightIdx, onSelect, onClose],
  );

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const portal = document.getElementById("ing-picker-portal");
      if (portal && !portal.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      id="ing-picker-portal"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: "340px",
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: "4px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        zIndex: 99999,
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        placeholder={t('ingredientSearch.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{
          padding: "6px 8px",
          fontSize: "12px",
          border: "none",
          borderBottom: "1px solid #e0e0e0",
          outline: "none",
          background: "#fafafa",
        }}
      />
      <div ref={listRef} style={{ maxHeight: "220px", overflowY: "auto" }}>
        {filtered.length === 0 && (
          <div
            style={{
              padding: "8px",
              fontSize: "11px",
              color: "#999",
              textAlign: "center",
            }}
          >
            {t('ingredientSearch.noMatches')}
          </div>
        )}
        {filtered.map((ing, idx) => (
          <div
            key={ing.id}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect(ing.id);
            }}
            onMouseEnter={() => setHighlightIdx(idx)}
            style={{
              padding: "4px 8px",
              fontSize: "11px",
              cursor: "pointer",
              background:
                idx === highlightIdx
                  ? "#e8f0fe"
                  : ing.id === currentValue
                    ? "#f0f7ff"
                    : "#fff",
              borderBottom: "1px solid #f0f0f0",
              lineHeight: "18px",
            }}
          >
            <span style={{ fontWeight: 600, color: "#333" }}>
              {ing.common_name}
            </span>
            <span style={{ color: "#888", marginLeft: "6px" }}>({ing.id})</span>
            {ing.supplier && (
              <span style={{ color: "#aaa", marginLeft: "6px" }}>
                - {ing.supplier}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>,
    document.body,
  );
}
